// konfiguracja axios'a (zapytania do bazy)

import axios from 'axios';
import { store, reduxActions } from 'store';
import i18n from 'i18n';
import { ICommandResponseError, ICommandResponseSuccess } from './types';
import remove from 'lodash/remove';
import find from 'lodash/find';

const {
  REACT_APP_API_BASE_URL,
  REACT_APP_API_BASE_PATH,
  REACT_APP_OPEN_PROFILE_CLIENT_ID,
  REACT_APP_OPEN_PROFILE_CLIENT_SECRET
} = process.env;

if (!REACT_APP_API_BASE_URL || !REACT_APP_API_BASE_PATH) {
  throw new Error('Specify REACT_APP_API_BASE_URL and REACT_APP_API_BASE_PATH in your .env file');
}

// nazwa pola z refresh tokenem w localStorage
const JWT_REFRESH_TOKEN = 'JWT_REFRESH_TOKEN';

const axiosInstance = axios.create();

axiosInstance.defaults.baseURL = `${REACT_APP_API_BASE_URL}${REACT_APP_API_BASE_PATH}`;
axiosInstance.defaults.headers.common['Accept'] = 'application/json';
axiosInstance.defaults.headers.post['Content-Type'] = 'application/json';

let refreshingPromise: Promise<string> | null = null;

const refreshTokens = async (error: any) => {
  if (refreshingPromise) {
    // Jeśli już trwa refresh — czekaj na niego i ponów request
    return refreshingPromise.then((newAccessToken) => {
      error.config.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(error.config);
    });
  }

  const refreshToken = localStorage.getItem(JWT_REFRESH_TOKEN);
  if (!refreshToken || refreshToken === 'undefined') {
    store.dispatch(reduxActions.signOut());
    return Promise.reject(new Error('Brak refresh tokena'));
  }

  const refreshTokenData = {
    refreshToken,
    clientId: REACT_APP_OPEN_PROFILE_CLIENT_ID || '',
    clientSecret: REACT_APP_OPEN_PROFILE_CLIENT_SECRET || ''
  };

  refreshingPromise = axios
    .post(
      `${REACT_APP_API_BASE_URL}${REACT_APP_API_BASE_PATH}/auth/refresh-token`,
      refreshTokenData
    )
    .then((response) => {
      const newAccessToken = response?.data?.access_token;
      const newRefreshToken = response?.data?.refresh_token;
      const newSessionId = response?.data?.session_id;

      if (!newAccessToken) {
        showMessage(response?.data?.hint, 'error');
        throw new Error(response?.data?.hint);
      }

      // Zapisz nowe tokeny
      store.dispatch(
        reduxActions.refreshTokens({
          token: newAccessToken,
          refreshToken: newRefreshToken,
          sessionId: newSessionId
        })
      );

      return newAccessToken;
    })
    .catch((err) => {
      // Jeśli refresh się nie powiedzie
      localStorage.removeItem(JWT_REFRESH_TOKEN);
      store.dispatch(reduxActions.signOut());
      throw err;
    })
    .finally(() => {
      refreshingPromise = null;
    });

  return refreshingPromise.then((newAccessToken) => {
    error.config.headers.Authorization = `Bearer ${newAccessToken}`;
    return axiosInstance(error.config);
  });
};

axiosInstance.interceptors.request.use((config) => {
  // aktualny język w headerze requestu
  const headers: { [key: string]: string } = {
    'Content-Language': i18n.language
  };

  // token autoryzacyjny w headerze requestu
  const authToken = store.getState().auth.token;
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  config.headers = {
    ...config.headers,
    ...headers
  };

  // id przelogowanego użytkownika
  const overloginUserId = store.getState().auth.overloginUserId;
  config.params = {
    ...config.params,
    switch_user_by_id: overloginUserId
  };

  return config;
});

// mapowanie błędu z api na standardowy typ błędu
const normalizeError = (
  error: Record<string, unknown>,
  httpStatus?: number
): ICommandResponseError => ({
  fields_info: error?.fields_info as ICommandResponseError['fields_info'],
  message:
    (error?.message as ICommandResponseError['message']) ||
    `Błąd serwera przetwarzania zapytania ${error?.detail && `(${error?.detail})`}`,
  message_style: error?.message_style as ICommandResponseError['message_style'],
  show_message:
    error?.show_message === undefined
      ? true
      : (error?.show_message as ICommandResponseError['show_message']),
  show_modal: (error?.show_modal as boolean) || false,
  status: 0,
  ...(httpStatus !== undefined && { httpStatus }),
  error_fields: error?.error_fields as ICommandResponseError['error_fields']
});

// mapowanie odpowiedzi z api (komenda) na standardowy typ
const normalizeSuccess = (data?: Record<string, unknown>): ICommandResponseSuccess => ({
  ...data,
  message: (data?.message as string) || 'Success',
  show_message: (data?.show_message as boolean) || false,
  message_style: data?.message_style as ICommandResponseSuccess['message_style'],
  fields_info: data?.fields_info as ICommandResponseSuccess['fields_info'],
  status: 1,
  data: data?.data as Record<string, unknown>
});

// pokazanie notyfikacji
const showMessage = (message: string, variant: 'success' | 'error' | 'warning') =>
  store.dispatch(reduxActions.setNotification({ message, variant }));

// pokazanie modala
const showModal = (title: string, content: string) =>
  store.dispatch(reduxActions.setNotificationModal({ title, content }));

// przechwycenie i zmodyfikowanie odpowiedzi z api
axiosInstance.interceptors.response.use(
  (response) => {
    // normalizacja typu komendy
    if (
      (response.config.method === 'post' ||
        response.config.method === 'put' ||
        response.config.method === 'delete') &&
      response.config.url !== '/auth/login' // TODO remove id response will be standard
    ) {
      if (response.data.status === 0) {
        const normalizedError = normalizeError(response.data);
        const fieldsInfoToShow = normalizedError.fields_info
          ? [...normalizedError.fields_info]
          : [];
        const text =
          fieldsInfoToShow
            ?.map((item) => `${item.property_name || item.property_path}: ${item.message}`)
            .join('\n') || '';
        // TO DO COMMENT THIS
        normalizedError.show_message &&
          showMessage(
            `${normalizedError.message}\n\n${i18n.t(
              'Poniższe pola nie spełniają koniecznych warunków'
            )}:\n${text}`,
            response.data.message_style
          );

        normalizedError.show_modal &&
          showModal(
            normalizedError.message,
            normalizedError.error_fields?.map((item) => item.message).join('<br />') || ''
          );
        return Promise.reject(normalizedError);
      }

      const normalizedSuccess = normalizeSuccess(response.data);

      const text =
        normalizedSuccess.fields_info
          ?.map((item) => `${item.property_name || item.property_path}: ${item.message}`)
          .join('\n') || '';

      normalizedSuccess.show_message &&
        showMessage(
          `${normalizedSuccess.message}${
            text && `\n\n${i18n.t('Poniższe pola nie spełniają koniecznych warunków')}:\n${text}`
          }`,
          response.data.message_style
        );
      return normalizedSuccess;
    }

    return response.data;
  },
  // normalizacja błędu
  async (error) => {
    if (error.response) {
      const { status } = error.response;

      // przeładowanie w trybie maintenance mode
      if (status === 503) {
        window.location.reload();

        return;
      }

      if (status === 401 && store.getState().auth.isAuthenticated) {
        try {
          error.config.__isRetryRequest = true;
          return await refreshTokens(error);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }

    const normalizedError = normalizeError(
      error.response?.data || error,
      error.response?.status
    );

    if (normalizedError.show_message) {
      if (normalizedError.fields_info) {
        const requestKeys = error?.config?.env?.FormData?.requestKeys || [];
        const fieldsInfoToShow = [...normalizedError.fields_info];
        remove(fieldsInfoToShow, (field) =>
          find(requestKeys, (key) => key === field.property_path)
        );
        const text =
          fieldsInfoToShow
            ?.map((item) => `${item.property_name || item.property_path}: ${item.message}`)
            .join('\n') || '';
        if (text.length) {
          showMessage(
            `${normalizedError.message}\n\n${i18n.t(
              'Poniższe pola nie spełniają koniecznych warunków'
            )}:\n${text}`,
            normalizedError.message_style || 'error'
          );
        } else {
          showMessage(normalizedError.message, normalizedError.message_style || 'error');
        }
      } else {
        showMessage(
          `${normalizedError.message} ${
            normalizedError.error_fields?.length
              ? normalizedError.error_fields.map((field) => field.message)
              : ''
          }`,
          normalizedError.message_style || 'error'
        );
      }
    }

    // TO DO COMMENT THIS

    normalizedError.show_modal &&
      showModal(
        normalizedError.message,
        normalizedError.error_fields?.map((item) => item.message).join('<br />') || ''
      );

    return Promise.reject(normalizedError);
  }
);

export default axiosInstance;
